import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { NEW_RULE_CONDITION_VALUE_TYPES, RULE_ACTION_TYPE } from 'Common/enums';
import { shouldShowExecutionOrder } from 'Selectors';
import { configService } from 'Services';
import {
    getCommaSeparatedCurrencyValue,
    getCommaSeparatedNumericValue,
    MAX_GLOBAL_CURRENCY,
    MIN_GLOBAL_CURRENCY,
    MAX_GLOBAL_THRESHOLD,
    ALPHANUMERIC_REGEX,
    WHITESPACE_REGEX,
    MIN_ADHOC_THRESHOLD
} from 'Utils';

export const ruleIdSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const ruleId = t('Rule ID');
    return Yup.string()
        .required(validationT('required', { field: ruleId }))
        .strict()
        .matches(WHITESPACE_REGEX, validationT('No whitespace allowed'))
        .matches(ALPHANUMERIC_REGEX, validationT('alphanumeric', { field: ruleId }))
        .min(1, validationT('minLength', { field: ruleId, charCount: 1 }))
        .max(20, validationT('maxLength', { field: ruleId, charCount: 20 }));
};

export const ruleGroupSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const ruleGroup = t('Rule Group');
    return Yup.array()
        .ensure()
        .required(validationT('required', { field: ruleGroup }))
        .min(1, validationT('minSelect', { field: ruleGroup, minSelect: 1 }));
};

export const descriptionSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const ruleDescription = t('Rule Description');
    return Yup.string()
        .required(validationT('required', { field: ruleDescription }))
        .trim(validationT('noLeadingTrailingSpaces'))
        .strict()
        .min(1, validationT('minLength', { field: ruleDescription, charCount: 1 }))
        .max(150, validationT('maxLength', { field: ruleDescription, charCount: 150 }))
        .test({
            name: 'ruleDescription',
            exclusive: true,
            message: validationT('backslashOrSingleQuote'),
            // eslint-disable-next-line
            test: (value) => value?.match("[/\\\\']") === null
        });
};

const thresholdSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionNumber = t('Value');
    return Yup.number()
        .required(validationT('required', { field: conditionNumber }))
        .min(
            MIN_ADHOC_THRESHOLD,
            validationT('minValue', {
                field: conditionNumber,
                minValue: getCommaSeparatedNumericValue(MIN_ADHOC_THRESHOLD)
            })
        )
        .max(
            MAX_GLOBAL_THRESHOLD,
            validationT('maxValue', {
                field: conditionNumber,
                maxValue: getCommaSeparatedNumericValue(MAX_GLOBAL_THRESHOLD)
            })
        );
};

const scoreSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionNumber = t('Value');
    return Yup.number()
        .required(validationT('required', { field: conditionNumber }))
        .integer(validationT('integer', { field: conditionNumber }))
        .min(
            configService.constants.minGlobalScore,
            validationT('minValue', {
                field: conditionNumber,
                minValue: configService.constants.minGlobalScore
            })
        )
        .max(
            configService.constants.maxGlobalScore,
            validationT('maxValue', {
                field: conditionNumber,
                maxValue: configService.constants.maxGlobalScore
            })
        );
};

export const actionsSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const ruleActions = t('Actions');
    return Yup.array(
        Yup.object({
            actionId: Yup.number().required(),
            operator: Yup.string().required(validationT('required', { field: ruleActions })),
            values: Yup.mixed().when('operator', (operator: Operator) => {
                switch (operator) {
                    case RULE_ACTION_TYPE.UPDATE_SCORE_BY:
                        return scoreSchema(t, validationT);
                    case RULE_ACTION_TYPE.SET_FLAGS:
                        return Yup.array()
                            .required(validationT('required', { field: ruleActions }))
                            .min(1, validationT('listminSelect', { field: 'flag', minSelect: 1 }));
                    case RULE_ACTION_TYPE.ADD_TO_LIST:
                        return Yup.object({
                            fieldName: Yup.string().required(),
                            listIds: Yup.array()
                                .required(validationT('required', { field: ruleActions }))
                                .min(1, validationT('listminSelect', { field: 'list', minSelect: 1 }))
                        });
                    default:
                        return Yup.mixed().notRequired().nullable();
                }
            })
        })
    );
};

const freeEntrySchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionValue = t('Value');
    return Yup.string().required(validationT('required', { field: conditionValue }));
};

const textSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionValue = t('Value');
    return Yup.string()
        .required(validationT('required', { field: conditionValue }))
        .min(1, validationT('minLength', { field: conditionValue, charCount: 1 }))
        .max(150, validationT('maxLength', { field: conditionValue, charCount: 150 }));
};

const currencySchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionValue = t('Value');
    return Yup.number()
        .required(validationT('required', { field: conditionValue }))
        .positive(validationT('integer', { field: conditionValue }))
        .min(
            MIN_GLOBAL_CURRENCY,
            validationT('minValue', {
                field: conditionValue,
                minValue: getCommaSeparatedCurrencyValue(MIN_GLOBAL_CURRENCY)
            })
        )
        .max(
            MAX_GLOBAL_CURRENCY,
            validationT('maxValue', {
                field: conditionValue,
                maxValue: getCommaSeparatedCurrencyValue(MAX_GLOBAL_CURRENCY)
            })
        );
};

const listSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const conditionList = t('Value');
    return Yup.array()
        .required(validationT('required', { field: conditionList }))
        .min(1, validationT('minSelect', { field: conditionList, minSelect: 1 }));
};

const conditionsSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const condition = t('Condition');
    const conditionParameter = t('Parameter');
    const conditionOperator = t('Operator');
    const conditionValue = t('Value');

    return Yup.array(
        Yup.object({
            parameterField: Yup.object({
                name: Yup.array().of(Yup.string()),
                type: Yup.string()
            }).required(validationT('required', { field: conditionParameter })),
            operatorField: Yup.array()
                .of(Yup.string())
                .required(validationT('required', { field: conditionOperator })),
            valueType: Yup.string(),
            valueField: Yup.mixed().when('valueType', (valueType: any): any => {
                switch (valueType) {
                    case NEW_RULE_CONDITION_VALUE_TYPES.STRING:
                        return textSchema(t, validationT);
                    case NEW_RULE_CONDITION_VALUE_TYPES.FREE_ENTRY:
                        return freeEntrySchema(t, validationT);
                    case NEW_RULE_CONDITION_VALUE_TYPES.NUMBER:
                        return thresholdSchema(t, validationT);
                    case NEW_RULE_CONDITION_VALUE_TYPES.CURRENCY:
                        return currencySchema(t, validationT);
                    case NEW_RULE_CONDITION_VALUE_TYPES.LIST:
                        return listSchema(t, validationT);
                    // when null, undefined, or something else
                    default:
                        return Yup.mixed().required(validationT('required', { field: conditionValue }));
                }
            }),
            readOnly: Yup.boolean()
        }).required(validationT('required', { field: condition }))
    );
};

const executionOrderSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const executionOrder = t('executionOrder');
    return Yup.number()
        .required(validationT('required', { field: executionOrder }))
        .min(
            1,
            validationT('minValue', {
                field: executionOrder,
                minValue: getCommaSeparatedNumericValue(1)
            })
        );
};

export const addRuleValidationSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    const status = t('Status');
    const showExecutionOrder = shouldShowExecutionOrder();
    return Yup.object({
        ruleId: ruleIdSchema(t, validationT),
        group: ruleGroupSchema(t, validationT),
        active: Yup.boolean().required(validationT('required', { field: status })),
        description: descriptionSchema(t, validationT),
        actions: actionsSchema(t, validationT),
        conditions: conditionsSchema(t, validationT),
        ...(showExecutionOrder && { priority: executionOrderSchema(t, validationT) })
    });
};
